import React, { useEffect, useState, useReducer, useContext, useCallback, useMemo, useRef, Dispatch, SetStateAction } from "react";
import { CheckIcon } from "@heroicons/react/24/solid";
import { TrashIcon } from "@heroicons/react/24/outline";
import { DateTime } from "luxon";
import InterfaceContext from "../Context";
import { legalNameValidator } from "../utils/nameValidator";
import { ValidatorMessage } from "../Components/ValidatorMessage";
import { InfoDict, QuestionProps } from "./Props";
import { classNames, safeParse } from "../Util";
import type { Role, Text, RichText, TargetFieldRef, CustomSelect, CustomDate, CustomTextEntry, HouseholdCalculatorQuestion, SelectFromData, CommonProperties } from "@aidkitorg/types/lib/survey";
import { v4 } from 'uuid';
import { useLocalizedStrings } from "../Localization";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, Badge, Button, Heading, Icon, Input, Select, TextArea, useToast } from "@aidkitorg/component-library";
import { usePost } from "../API";
import { MultipleSelectQuestion } from "./MultipleSelect";
import { SingleSelectQuestion } from "./SingleSelect";
import { SelectOptionType } from "@aidkitorg/component-library/dist/types/src/Select";
import { evalDistroConditional } from "@aidkitorg/types/lib/eval";
import * as v0 from "@aidkitorg/types/lib/survey";

export type HouseholdMember = {
  id: string;
  roleType: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  birth_date?: string;
  birth_year?: string;
  dependence?:
  | "independent"
  | "dependent_on_applicant"
  | "dependent_on_someone_else"
  | "applicant_dependent_on_them"
  | "";
  [key: string]: string | undefined;
};

type HouseholdAction = { type: 'add' | 'edit' | 'delete'; payload: HouseholdMember }
type MemberAction = { type: 'set'; payload: { key: string, value: string } }

type Date =
  | {
    year: number;
    month: number;
    day: number;
  }
  | TargetFieldRef
  | undefined;

function getReferenceDateMs(date: Date, info: InfoDict): number {
  // if no reference date is set, use "now"
  if (!date) {
    return new Date().getTime();
  }

  // if a reference date is a string, it's a target field name, so the field value is the date
  if (typeof date === 'string') {
    const newDate = new Date(info[date] || '').getTime();

    // if the field doesn't exist or the value can't be parsed as a date, return 0 to indicate a problem
    if (!info[date] || isNaN(newDate)) {
      return 0;
    }

    return newDate;
  }

  // otherwise, the reference date is a fixed date
  return new Date(`${date.month}/${date.day}/${date.year}`).getTime();
};

const checkValidLength = (value: string, question?: HouseholdCalculatorQuestion) => {
  // we don't care about the length of encrypted text
  if (value.startsWith('gAAA')) {
    return true;
  }
  
  if (question?.kind === 'TextEntry') {
    if (question?.minLength && value.length < question.minLength) {
      return false;
    }
    if (question?.maxLength && value.length > question.maxLength) {
      return false;
    }
  }
  return true;
}

function MemberForm(props: {
  roleInfo: Role;
  memberInfo: HouseholdMember;
  householdDispatch: React.Dispatch<HouseholdAction>;
  isSaved?: boolean;
  referenceDateMs: number;
  isUnique: (key: string, value: string, memberId: string) => boolean,
  collapseCallback?: (id: string, collapsed: boolean) => void;
  collapsible?: boolean;
  blockQuestionEdits?: boolean;
}) {
  const L = useLocalizedStrings();
  const {
    collectBirthdate,
    requireFullName,
    hideName,
    dependence,
    customQuestions,
  } = props.roleInfo.dataCollectOptions || {};
  const validateFullName = typeof requireFullName === 'boolean' ? requireFullName : requireFullName === 'Full name';
  const hhContext = useContext(HouseholdContext);

  const reducer = (state: HouseholdMember, action: MemberAction) => {
    switch (action.type) {
      case "set":
        return {
          ...state,
          [action.payload.key]: action.payload.value,
        };
      default:
        return state;
    }
  };
  const [memberValidState, memberDispatch] = useReducer(reducer, props.memberInfo);
  const [ageLabel, setAgeLabel] = useState("");
  const firstLoad = useRef(true);
  const recentlySaved = useRef(false);
  const previousValidState = useRef(JSON.stringify(memberValidState));
  const context = useContext(InterfaceContext);

  useEffect(() => {
    if (props.blockQuestionEdits) return;
    
    // Keep household form state current with valid values from member forms
    const newValidState = JSON.stringify(memberValidState);
    if (newValidState !== previousValidState.current) {
      previousValidState.current = newValidState;
      props.householdDispatch({
        type: "edit",
        payload: memberValidState,
      });
    }
  }, [memberValidState, props.blockQuestionEdits]);

  useEffect(() => {
    if (!firstLoad.current && props.isSaved) {
      recentlySaved.current = true;
    }
    firstLoad.current = false;
  }, [props.isSaved]);

  return (
    <fieldset disabled={props.blockQuestionEdits}>
      <div className="w-full flex flex-col items-end gap-2 px-1 py-2">
        {!hideName && (
          <div className="w-full flex flex-col sm:flex-row sm:items-center">
            <Name
              label={
                requireFullName === 'Split first and last'
                  ? L.questions.household.first_name
                  : (validateFullName
                    ? L.questions.household.full_name
                    : L.questions.household.name)
              }
              id={memberValidState.id}
              value={(requireFullName === 'Split first and last' 
                ? memberValidState.first_name
                : memberValidState.name) || ''}
              saveKey={requireFullName === 'Split first and last' ? 'first_name' : 'name'}
              requireFullName={validateFullName}
              updateMember={(key, value) => {
                memberDispatch({
                  type: "set",
                  payload: {
                    key,
                    value,
                  },
                });
              }}
            />
          </div>
        )}
        {!hideName && requireFullName === 'Split first and last' && (
          <div className="w-full flex flex-col sm:flex-row sm:items-center">
            <Name
              label={L.questions.household.last_name}
              id={memberValidState.id}
              value={memberValidState.last_name || ''}
              saveKey={'last_name'}
              requireFullName={false}
              updateMember={(key, value) => {
                memberDispatch({
                  type: "set",
                  payload: {
                    key,
                    value,
                  },
                });
              }}
            />
          </div>
        )}

        {collectBirthdate === "full" && (
          <div className="w-full">
            <div className="sm:border-gray-200">
              <div className="flex flex-col w-full">
                <label
                  htmlFor={"birth_month_" + memberValidState.id}
                  className="text-sm font-medium text-gray-700 mb-0.5 flex-none w-28 sm:mt-1"
                >
                  {L.questions.household.birth_date} 
                  <span className="text-red-600" aria-hidden="true"> *</span>
                  <span className="sr-only"> required</span>
                </label>
              </div>
              <div className="w-full flex-1">
                <Birthdate
                  id={memberValidState.id}
                  value={memberValidState.birth_date || ""}
                  referenceDateMs={props.referenceDateMs}
                  updateMember={(key, value) => {
                    memberDispatch({
                      type: "set",
                      payload: { key, value },
                    });
                  }}
                  setAgeLabel={(label) => setAgeLabel(label)}
                  blockQuestionEdits={props.blockQuestionEdits}
                />
                {ageLabel && (
                  <div className="text-sm mt-2 font-medium text-gray-500">
                    {ageLabel}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {collectBirthdate === "year_only" && (
          <div className="col-span-2 sm:gap-4 w-full sm:pt-2">
            <BirthYear
              memberId={memberValidState.id}
              value={memberValidState.birth_year || ""}
              updateMember={(key, value) => {
                memberDispatch({ type: "set", payload: { key, value } });
              }}
            />
          </div>
        )}

        {dependence === "collect" && (
          <div className="w-full flex flex-col sm:flex-row sm:items-center">
            <label
              htmlFor={"dependence_" + props.memberInfo.id}
              className="block text-sm font-medium text-gray-700 mb-1 sm:flex-none sm:w-28 sm:mt-1"
            >
              {L.questions.household.financial_dependence}
            </label>
            <select
              id={"dependence_" + props.memberInfo.id}
              name="dependence"
              className={
                "sm:mt-0 block w-full rounded-md px-2 h-10 cursor-pointer text-base bg-white appearance-none " +
                (memberValidState.dependence
                  ? "text-black border-2 border-green-200 focus:ring-green-400 ring-green-400 focus:border-green-400"
                  : "text-gray-500 border-2 border-red-200 focus:ring-red-400 ring-red-400 focus:border-red-400")
              }
              value={memberValidState.dependence || "--"}
              onChange={(e) => {
                memberDispatch({
                  type: "set",
                  payload: { key: "dependence", value: e.target.value },
                });
              }}
            >
              <option value="--" disabled hidden>{L.questions.household.please_select_one}</option>
              <option value="independent">{L.questions.household.independent}</option>
              <option value="dependent_on_applicant">{L.questions.household.dependent_on_me}</option>
              <option value="dependent_on_someone_else">{L.questions.household.dependent_on_someone_else}</option>
              <option value="applicant_dependent_on_them">{L.questions.household.im_dependent_on_them}</option>
            </select>
          </div>
        )}

        {customQuestions?.map((question, index) => {

          // If a question is conditional, we need to evaluate the conditional and only show the question if the conditional is met
          if (question.conditionalOn) {
            let conditional: v0.BooleanExpr = typeof question.conditionalOn === 'string' ? { kind: 'Code Boolean Expr', code: question.conditionalOn } : question.conditionalOn;
            let conditionalIsMet = evalDistroConditional(conditional, { ...hhContext?.parentProps.info, ...props.memberInfo });
            if (!conditionalIsMet) return null;
          }

          if (question.kind in HouseholdQuestionMap) {
            const questionProps: HouseholdQuestionProps<any> = {
              key: props.memberInfo.id + '_' + getKey(question) + '_' + index,
              question: question as any,
              index,
              memberValidState,
              memberDispatch
            }

            return <div key={questionProps.key} className="w-full flex flex-col sm:flex-row sm:items-center">
              {React.createElement(
                HouseholdQuestionMap[question.kind as keyof typeof HouseholdQuestionMap] as any, 
                questionProps)}
            </div>
          }

          if (question.kind === "TextEntry") {
            return (
              <div key={question.key + "_" + index} className="w-full">
                {question.encrypted
                  ? <EncryptedTextEntry 
                    question={question} 
                    updateMember={(key, value) => {
                      memberDispatch({
                        type: "set",
                        payload: { key, value },
                      });
                    }}
                    initialValue={memberValidState[question.key] || ''}
                  />
                  : <CustomText 
                    id={index} 
                    value={memberValidState[question.key] || ''} 
                    question={question}
                    memberId={memberValidState.id}
                    isUnique={props.isUnique}
                    updateMember={(k, v) => {
                      memberDispatch({
                        type: "set",
                        payload: { key: k, value: v },
                      });
                    }}
                  />
                }
              </div>
            )
          }

          if (question.kind === 'Date') {
            return (
              <div key={question.key + "_" + index} className="w-full">
                <CustomDate
                  label={(question.content)[context.lang as keyof RichText] ?? ""}
                  id={memberValidState.id}
                  value={(memberValidState[question.key] || memberValidState.birth_date) || ""}
                  question={question}
                  referenceDateMs={props.referenceDateMs}
                  updateMember={(key, value) => {
                    memberDispatch({
                      type: "set",
                      payload: { key, value },
                    });
                  }}
                  blockQuestionEdits={props.blockQuestionEdits}
                />
              </div>
            );
          }
        })}

        <div className="w-full">
          {props.isSaved && (
            <Badge variant="success" className="w-full">
              <div className="flex flex-row gap-x-2 items-center text-sm">
                <Icon name="CheckCircleIcon" type="solid" size="md" />
                <span>{L.questions.household.all_fields_completed}</span>
              </div>
            </Badge>
          )}
        </div>

        {!props.blockQuestionEdits && <div className="w-full flex justify-between gap-x-2">
          <Button
            variant="danger"
            icon={<Icon name="TrashIcon" />}
            onClick={() =>
              props.householdDispatch({
                type: "delete",
                payload: memberValidState,
              })
            }
          >
            {L.questions.household.remove}
          </Button>
        </div>}
      </div>
    </fieldset>
  );
}

function MemberDisplayGroup(props: {
  label: string;
  members: HouseholdMember[];
}) {
  return (
    <div className="my-3 sm:mx-3">
      {props.label && (
        <div className="flex flex-1 gap-2 text-gray-700 font-bold text-xl mt-4 mb-2">
          {props.label}
        </div>
      )}
      <div className="px-2 py-4 w-full mr-0 rounded-lg border border-solid">
        {props.members.map((member) => (
          <div key={member.id} className="w-full flex justify-between items-end">
            <div key={member.id} className=" sm:px-3 flex flex-col gap-1">
              <div className="text-gray-700">{member.name}</div>
              {member.birth_date && (
                <div className="text-gray-400 text-sm">{member.birth_date}</div>
              )}
            </div>
            <div/>
            <CheckIcon className="sm:mx-3 h-7 w-7 text-base text-green-500" />
          </div>
        ))}
      </div>
    </div>
  );
}

const msPerDay = 1000 * 60 * 60 * 24;

function memberIsValid(householdMember: HouseholdMember, 
  info: InfoDict,
  dataCollectOptions?: v0.HouseholdCalculator['roles'][number]['dataCollectOptions']) {
  return Object.keys(householdMember).every((k) => {
    const question = dataCollectOptions?.customQuestions?.find((q: HouseholdCalculatorQuestion) => (getKey(q) === k));
    if (question?.conditionalOn) {
      let conditional: v0.BooleanExpr = typeof question.conditionalOn === 'string' ? { kind: 'Code Boolean Expr', code: question.conditionalOn } : question.conditionalOn;
      let conditionalIsMet = evalDistroConditional(conditional, { ...info, ...householdMember });
      // console.log("conditionalIsMet", conditionalIsMet, question.conditionalOn, householdMember);
      if (!conditionalIsMet) return true;
    }

    const isValid = (householdMember[k] || !!question?.optional) && checkValidLength(householdMember[k] || '', question);
    
    return isValid;
  });
}

/**
 * Get the key for a question. 
 * @returns string: the question.key or question.targetField, depending on the question type.
 */
function getKey(question: HouseholdCalculatorQuestion) {
  return 'key' in question ? question.key : question.targetField;
}

function MemberFormGroup(props: {
  info: InfoDict;
  roleInfo: Role;
  membersWithForm: HouseholdMember[];
  membersSaved: HouseholdMember[];
  referenceDateMs: number;
  householdDispatch: React.Dispatch<HouseholdAction>;
  initializeMemberInfo: (role: Role) => HouseholdMember;
  blockQuestionEdits?: boolean;
}) {
  const L = useLocalizedStrings();
  const context = useContext(InterfaceContext);
  const { roleInfo, membersWithForm, membersSaved, householdDispatch, referenceDateMs } = props;
  const { label, dataCollectOptions, displayOptions } = roleInfo;
  const [collapsedMap, setCollapsedMap] = useState(membersWithForm.reduce((acc, obj) => {
    acc[obj.id] = membersWithForm.length > 1; // if there's only one member, leave form open
    return acc;
  }, {} as Record<string, boolean>));

  let maxCount: number | undefined;
  // check if maxCount has been set
  if (typeof dataCollectOptions.maxCount !== undefined && dataCollectOptions.maxCount !== null) {
    const maxCountTemp = dataCollectOptions.maxCount
    // if it's a number set it
    if (typeof maxCountTemp === 'number') {
      maxCount = maxCountTemp
    // otherwise look for a TargetFieldRef with a value that can be parsed to an int
    } else if (maxCountTemp && maxCountTemp['field'] && props.info[maxCountTemp['field']] !== undefined && props.info[maxCountTemp['field']] !== '') {
      maxCount = !isNaN(parseInt(props.info[maxCountTemp['field']] || '')) ? parseInt(props.info[maxCountTemp['field']] || '') : undefined;
    }
  }

  const allRequiredFormFieldsComplete = useMemo(() => 
    membersWithForm.every((member) => memberIsValid(member, props.info, dataCollectOptions)), [membersWithForm, dataCollectOptions, props.info]);


  // map from question key to values already inputted for that question
  const isUnique = useCallback((key: string, value: string, memberId: string) => {
    const uniques: Record<string, Set<string>> = {};
    membersWithForm.forEach(m => {
      // skip your own answers.
      if (m.id === memberId) return;

      dataCollectOptions.customQuestions?.forEach(q => {
        if (q.kind === 'TextEntry' && q.unique && m[q.key]) {
          uniques[q.key] = (uniques[q.key] || new Set<string>()).add(m[q.key] as string);
        }
      })
    });
    
    return !(uniques[key] || new Set<string>()).has(value);
  }, [membersWithForm, dataCollectOptions]);

  let roleCountText = "";
  if (!displayOptions.hideCount) {
    if (membersSaved.length === 1) {
      roleCountText = "1 " + label.singular[context.lang];
    } else {
      roleCountText = membersSaved.length + " " + (label?.plural?.[context.lang] || label.singular[context.lang]);
    }
  }

  const collapseCallback = (id: string, collapsed: boolean) => {
    if (!displayOptions.collapseEntries) return;

    setCollapsedMap(prev => {
      return {
        ...prev,
        [id]: collapsed
      }
    })
  };

  const addMember = useCallback(() => {
    if (props.blockQuestionEdits) return;

    const lastMember = membersWithForm?.[membersWithForm.length - 2]?.id;
    if (lastMember) {
      setCollapsedMap(prev => {
        return {
          ...prev,
          [lastMember]: true
        }
      });
    }

    const newMember = props.initializeMemberInfo(roleInfo) as HouseholdMember;
    householdDispatch({
      type: "add",
      payload: newMember,
    });

    setCollapsedMap(prev => {
      return {
        ...prev,
        [newMember.id]: false
      }
    });
  }, [roleInfo, membersWithForm, props.blockQuestionEdits]);

  const getDisplayField = (member: HouseholdMember, i: number) => {
    const displayField = displayOptions.collapseEntries?.displayField;

    if (displayField && displayField === 'name') {
      return member.name || (member.first_name + ' ' + member.last_name);
    }

    return displayField && member[displayField] ? member[displayField] : `${roleInfo.label.singular[context.lang]} ${i + 1}`;
  }

  return (
    <div className="mt-4 mb-2.5 sm:mx-3">
      <div className="flex-1 flex text-gray-700 font-bold text-xl my-2">
        {maxCount === 1 || !label.plural
          ? label.singular[context.lang] : label.plural[context.lang]}
      </div>
      <div className="flex flex-col w-full gap-2.5 sm:gap-3">
        {displayOptions.collapseEntries
          ? <Accordion type='multiple' value={Object.keys(collapsedMap).filter(k => !collapsedMap[k])}>
            {membersWithForm.map((member, i) => (
              <AccordionItem key={member.id} value={member.id} className="px-2.5 sm:px-4 mb-2 rounded-md border border-solid border-gray-200">
                <AccordionTrigger className="text-sm" onClick={() => collapseCallback(member.id, !collapsedMap[member.id])}>
                  <span>{getDisplayField(member, i)}</span>
                </AccordionTrigger>
                <AccordionContent>
                  <MemberForm
                    key={member.id}
                    roleInfo={props.roleInfo}
                    memberInfo={member}
                    isSaved={membersSaved.some((p) => p.id === member.id)}
                    referenceDateMs={referenceDateMs}
                    householdDispatch={householdDispatch}
                    isUnique={isUnique}
                    collapseCallback={collapseCallback}
                    collapsible={!!displayOptions.collapseEntries}
                    blockQuestionEdits={props.blockQuestionEdits}
                  />
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          : membersWithForm.map((member, i) => (
            <div key={member.id} className="pb-1 pt-3 px-2.5 sm:px-4 last:pb-0 rounded-md border border-solid border-gray-200">
              <MemberForm
                key={member.id}
                roleInfo={props.roleInfo}
                memberInfo={member}
                isSaved={membersSaved.some((p) => p.id === member.id)}
                referenceDateMs={referenceDateMs}
                householdDispatch={householdDispatch}
                isUnique={isUnique}
                blockQuestionEdits={props.blockQuestionEdits}
              />
            </div>
          ))
        }
      </div>
      <div className="flex flex-col gap-1.5">
        {!displayOptions.hideCount && <div className="mt-2.5 text-[17px] text-gray-500">
          {roleCountText}
        </div>}
        {(maxCount === undefined || membersWithForm.length < maxCount) && !props.blockQuestionEdits && (
          <div>
            <Button 
              variant="secondary"
              onClick={addMember}
              isDisabled={!allRequiredFormFieldsComplete}
              icon={<Icon name="PlusIcon" />}
            >
              {L.questions.household.add} {roleInfo.label.singular[context.lang]}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function Name(props: {
  id: string;
  value: string;
  requireFullName?: boolean;
  saveKey?: string;
  label: string;
  updateMember: (key: string, value: string) => void;
}) {
  const L = useLocalizedStrings();
  const [value, setValue] = useState(props.value || "");
  const [valid, setValid] = useState(false);
  const [pending, setPending] = useState(true);
  let validatorMessage;
  let validator = (v: string) => v.length > 0;
  if (props.requireFullName) {
    validatorMessage = L.questions.textentry.please_enter_your_legal_name;
    validator = (v) => legalNameValidator(v);
  }
  function checkIfValid(formValue?: string) {
    const isValid = validator(formValue ?? value);
    setValid(isValid);
    props.updateMember(props.saveKey || "name", isValid ? formValue || value : "");
  }

  useEffect(() => {
    checkIfValid();
  }, []);
  return (
    <fieldset className="flex-1">
      <Input 
        label={props.label}
        value={value}
        id={`name_${props.id}`}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setValue(e.target.value);
          checkIfValid(e.target.value);
        }}
        onBlur={(e) => {
          if (pending) setPending(false);
          checkIfValid(e.target.value);
        }}
        required
      />
      {!pending && !valid && validatorMessage && (
        <div className="text-sm">
          <ValidatorMessage message={validatorMessage as string} />
        </div>
      )}
    </fieldset>
  );
}

function BirthYear(props: {
  value: string;
  memberId: string;
  updateMember: (key: string, value: string) => void;
}) {
  const L = useLocalizedStrings();
  const [year, setYear] = useState(props.value || "");
  const [error, setError] = useState("");
  const [pending, setPending] = useState(true);

  const yearDifference = new Date().getFullYear() - parseInt(year);
  const inFuture = yearDifference < 0;
  const valid = yearDifference <= 125;

  useEffect(() => {
    if (!valid) {
      setError(L.questions.household.invalid_year);
    } else if (inFuture) {
      setError(L.questions.household.must_be_in_the_past);
    } else {
      setError('');
    }
    props.updateMember("birth_year", (valid && !inFuture) ? year : "");
  }, [year, inFuture, valid]);


  return (
    <fieldset className="flex-1">
      <Input 
        required
        label={L.questions.household.birth_year}
        type="number"
        value={year}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          if (e.target.value.length === 4) setPending(false);
          setYear(e.target.value.slice(0, 4));
        }}
        onBlur={() => setPending(false)}
        placeholder={L.date.yyyy}
        error={{
          hasError: (!pending && !!error),
          errorMessage: error,
        }}
      />
    </fieldset>
  );
}

function Birthdate(props: {
  id: string;
  value: string;
  referenceDateMs: number;
  updateMember: (key: string, value: string) => void;
  setAgeLabel: (ageLabel: string) => void;
  blockQuestionEdits?: boolean;
}) {
  const prev = (props.value || "").match(
    /^(01|02|03|04|05|06|07|08|09|10|11|12)\/([0-3][0-9])\/((19|20)[0-9][0-9])$/
  );

  const L = useLocalizedStrings();
  const [month, setMonth] = React.useState(prev ? prev[1] : "--");
  const [day, setDay] = React.useState(prev ? prev[2] : "");
  const [year, setYear] = React.useState(prev ? prev[3] : "");
  const [pending, setPending] = React.useState(!prev);
  const [error, setError] = React.useState("");

  const enteredDateMs = new Date(`${month}/${day}/${year}`).getTime();
  
  const ageNumber = Math.floor((props.referenceDateMs - enteredDateMs) / (365.25 * msPerDay))

  const validStatus = pending
    ? "incomplete"
    : DateTime.fromISO(`${year}-${month}-${day}`).invalidReason || ageNumber > 125
      ? "invalid"
      : enteredDateMs > props.referenceDateMs
        ? "inFuture"
        : "valid";

  let ageLabel = "";  
  if (validStatus === "valid") {
    const ageDescription =
      ageNumber <= 1
        ? " " + L.questions.household.year_old
        : " " + L.questions.household.years_old;
    ageLabel =
      ageNumber < 1
        ? "< 1" + ageDescription
        : JSON.stringify(ageNumber) + ageDescription;
  }

  useEffect(() => {
    if (validStatus === "incomplete") {
      return;
    }
    if (validStatus === "valid") {
      setError("");
      props.updateMember("birth_date", `${month}/${day}/${year}`);
      // Save age at the time birthdate is set for #sifh application (consider removing after June 2023)
      props.updateMember("age", JSON.stringify(ageNumber));
    } else {
      if (validStatus === "inFuture") {
        setError(L.questions.household.must_be_in_the_past);
      } else {
        setError(L.questions.household.invalid_date);
      }
      props.updateMember("birth_date", "");
      props.updateMember("age", "")
    }
    props.setAgeLabel(ageLabel);
  }, [day, month, year, validStatus]);

  const monthOptions = useMemo(() => [
    { label: L.months.jan, value: "01" },
    { label: L.months.feb, value: "02" },
    { label: L.months.mar, value: "03" },
    { label: L.months.apr, value: "04" },
    { label: L.months.may, value: "05" },
    { label: L.months.jun, value: "06" },
    { label: L.months.jul, value: "07" },
    { label: L.months.aug, value: "08" },
    { label: L.months.sep, value: "09" },
    { label: L.months.oct, value: "10" },
    { label: L.months.nov, value: "11" },
    { label: L.months.dec, value: "12" }
  ], [L.months]);

  const selectedMonth = useMemo(() => 
    monthOptions.find((monthOption) => monthOption.value === month), 
  [monthOptions, month]
  );

  return (
    <fieldset>
      <div className="relative flex justify-between gap-2 sm:gap-3 w-full sm:visible sm:flex-wrap">
        <div className="flex-grow basis-32 mt-[1px]">
          <Select 
            description=""
            label={L.questions.household.month} 
            labelLocation="above"         
            value={selectedMonth}
            onChange={(option) => setMonth(option.value)}
            options={monthOptions}
            disabled={props.blockQuestionEdits}
          />
        </div>
        <div>
          <Input 
            label={L.questions.household.day}
            type="number"
            value={day}
            onChange={(e) => setDay(e.target.value.slice(0, 2))}
            onBlur={(e) => {
              if (e.target.value.length === 1) {
                setDay("0" + e.target.value);
              }
            }}
            min={1}
            max={31}
            placeholder={L.date.dd}
            disabled={props.blockQuestionEdits}
          />
        </div>
        <div className="flex-grow basis-8">
          <Input
            pattern="[1-2][0-9]{3}"
            label={L.questions.household.year}
            type="number"
            placeholder={L.date.yyyy}
            value={year}
            onChange={(e) => {
              setYear(e.target.value.slice(0, 4));
              if (e.target.value.length === 4) setPending(false);
            }}
            onBlur={(e) => {
              if (e.target.value.length > 0) setPending(false);
            }}
            disabled={props.blockQuestionEdits}
          />
        </div>
      </div>
      {error && !pending && (
        <div className="text-sm">
          <ValidatorMessage message={error} />
        </div>
      )}
    </fieldset>
  );
}

function CustomText(props: {
  id: number;
  question: any;
  value: string;
  memberId: string;
  isUnique: (key: string, value: string, memberId: string) => boolean,
  updateMember: (key: string, value: string) => void;
}) {
  const L = useLocalizedStrings();
  const context = useContext(InterfaceContext);
  const [value, setValue] = useState<string>('');
  const [validationMessage, setValidationMessage] = useState('');
  const [isValid, setIsValid] = useState<boolean>(true);

  useEffect(() => {
    setValue(props.value);
  }, [props.value])

  const sanitizeInput = (value: unknown) => {
    const stringValue = value == null ? '' : String(value);

    if (props.question.inputType &&
      ((typeof props.question.inputType === 'object' &&
        'kind' in props.question.inputType &&
        props.question.inputType.kind === 'currency') 
        || (props.question.inputType === 'percentage')
        || (props.question.inputType === 'number'))) {
      // If the question was configured with an input type of number, percent, or currency, 
      // allow numbers and one decimal point
      return stringValue.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1"); // Keep only the first decimal point
    } else if (props.question.formatting === 'numbers only' || props.question.formatting === 'phone') {
      return stringValue.replace(/[^0-9]/g, "");
    }
    return stringValue;
  }

  const validateInput = useCallback((value: unknown) => {
    const stringValue = value == null ? '' : String(value);
    let valid = true;
    let newValidationMessage = '';

    // only test validation when an input has actually been entered.
    if (stringValue.length > 0) {
      if (props.question.formatting === 'phone') {
        valid = stringValue.length >= 10 && stringValue.length <= 12 
          && !stringValue.startsWith('0') && !stringValue.startsWith('1');
        if (!valid) newValidationMessage = L.questions.textentry.please_enter_valid_us_number;
      } else if (props.question.formatting === 'email') {
        valid = !!stringValue.match(
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        )
        if (!valid) newValidationMessage = L.questions.textentry.must_be_a_valid_email;
      }
  
      // only check uniqueness after we've passed the other formatting
      if (valid && props.question.unique) {
        valid = props.isUnique(props.question.key, stringValue, props.memberId);
        if (!valid) newValidationMessage = L.questions.textentry.is_duplicate;
      }
    }

    if (newValidationMessage !== validationMessage) {
      setValidationMessage(newValidationMessage);
    }

    const validLength = checkValidLength(stringValue, props.question);

    setIsValid(valid && validLength);
    return valid && validLength;
  }, [props.isUnique, props.question, props.memberId])

  const inputType = props.question?.inputType ?? "text";

  const getFormattingProps = (inputType: CustomTextEntry['inputType']): Record<string, any> => {
    if (inputType && typeof inputType === 'object' && 'kind' in inputType && inputType.kind === 'currency') {
      const currencyCode = inputType.currency || 'USD'; 
      let currencySymbol = '$';
      try {
        const parts = new Intl.NumberFormat(context.lang, { style: 'currency', currency: currencyCode }).formatToParts(0);
        const currencyPart = parts.find(part => part.type === 'currency');
        if (currencyPart) {
          currencySymbol = currencyPart.value;
        }
      } catch (e) {
        console.warn(`Invalid currency code: ${currencyCode}`);
      }

      // Get proper decimal scale for the currency
      let decimalScale = 2; // Default for most currencies
      try {
        // Check actual decimal usage for this currency
        const formatted = new Intl.NumberFormat(context.lang, { style: 'currency', currency: currencyCode }).format(1);
        const hasDecimals = formatted.includes('.');
        if (!hasDecimals) {
          decimalScale = 0;
        }
      } catch (e) {
        // Keep default 2
      }

      return {
        isCurrency: true,
        currencySymbol: currencySymbol,
        decimalScale: decimalScale,
        numberFormatProps: {
          fixedDecimalScale: true
        }
      };
    }

    if (inputType === 'phone number') {
      return {
        isPatternFormat: true,
        formatPattern: "(###) ###-####",
        mask: "_",
      }
    }

    if (inputType === 'percentage') {
      return {
        isNumeric: true,
        suffix: "%",
      }
    }

    if (inputType === 'number') {
      return {
        isNumeric: true,
        thousandSeparator: true,
      }
    }
    
    return {};
  }

  const formattingProps = getFormattingProps(inputType);

  return <div className="flex flex-col w-full mb-1">
    {
      inputType === 'textarea'
        ? <TextArea 
          required={!props.question.optional}
          label={(props.question.content)[context.lang as keyof RichText]} 
          showLabel
          value={value}
          id={props.question.key + "_" + props.id}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
            const newVal = sanitizeInput(e.target.value);

            // after you've reached the limit, disallow typing any more characters.
            if (props.question.maxLength && newVal.length > props.question.maxLength) {
              return;
            }
            setValue(newVal);
            if (validateInput(e.target.value)) {
              props.updateMember(props.question.key, newVal);
            }
          }}
          minLength={props.question.minLength}
          maxLength={props.question.maxLength}
        />
        : <Input 
          required={!props.question.optional}
          label={(props.question.content)[context.lang as keyof RichText]}
          value={value}
          id={props.question.key + "_" + props.id}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const newVal = sanitizeInput(e.target.value);

            // after you've reached the limit, disallow typing any more characters.
            if (props.question.maxLength && newVal.length > props.question.maxLength) {
              return;
            }
            setValue(newVal);
            if (validateInput(e.target.value)) {
              props.updateMember(props.question.key, newVal);
            }
          }}
          {...formattingProps}
          min={props.question.min}
          max={props.question.max}
          minLength={props.question.minLength}
          maxLength={props.question.maxLength}
        />
    }
    {!isValid && validationMessage && (
      <div className="text-sm">
        <ValidatorMessage message={validationMessage} />
      </div>
    )}
  </div>
}

  
function EncryptedTextEntry(props: {
  question: any;
  updateMember: (key: string, value: string) => void;
  initialValue: string;
}) {

  const encrypt = usePost('/encrypt');
  const [unencryptedValue, setUnencryptedValue] = useState<string>();
  const timeoutRef = useRef<NodeJS.Timeout>();
  const [showValue, setShowValue] = useState(false);
  const context = useContext(InterfaceContext);

  const sendUpdate = useCallback((key: string, value: string) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(async () => {
      const encryptedValue = await encrypt({ text: value, withHash: true });
      props.updateMember(key, encryptedValue.value);
      if (encryptedValue.hash) props.updateMember(key + '_hash', encryptedValue.hash);
    }, 300);
  }, []);

  const onInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    let toSend = e.target.value;
    if (unencryptedValue === undefined) {
      toSend = e.target.value.replace('whatever', '');
    }
    setUnencryptedValue(toSend);
    // only send update if the input is valid - that way the parent can trust that this is ready to be saved.
    if (checkValidLength(toSend, props.question)) {
      sendUpdate(props.question.key, toSend);
    }
  }, []);

  const alreadyEncrypted = useMemo(() => {
    return props.initialValue.startsWith('gAAA') && unencryptedValue === undefined
  }, [props.initialValue, unencryptedValue]);

  return <div className='flex flex-row justify-between w-full gap-x-2'>
    <div className="flex-grow">
      <Input
        required={!props.question.optional}
        type={showValue ? "input" : "password"}
        label={(props.question.content)[context.lang as keyof RichText]}
        value={alreadyEncrypted ? "whatever" : unencryptedValue}
        onChange={onInputChange}
        // We will not send the encrypted value if it's already saved and will only
        // display a fake value. Don't enforce length constraints in this case.
        minLength={alreadyEncrypted ? undefined : props.question.minLength}
        maxLength={alreadyEncrypted ? undefined : props.question.maxLength}
      />
    </div>
    <div className="mt-[2rem]">
      {!alreadyEncrypted && (showValue
        ? <button onClick={() => setShowValue(!showValue)}>
          <Icon name="EyeSlashIcon" role="decorative" size="md" />
        </button>
        : <button onClick={() => setShowValue(!showValue)}>
          <Icon name="EyeIcon" role="decorative" size="md" />
        </button>)
      }
    </div>
  </div>
}

function CustomDate(props: {
  id: string;
  label: string;
  value: string;
  question: any;
  referenceDateMs: number;
  updateMember: (key: string, value: string) => void;
  blockQuestionEdits?: boolean;
}) {
  const prev = (props.value || "").match(
    /^(01|02|03|04|05|06|07|08|09|10|11|12)\/([0-3][0-9])\/((19|20)[0-9][0-9])$/
  );
  const context = useContext(InterfaceContext);
  const L = useLocalizedStrings();
  const [month, setMonth] = React.useState(prev ? prev[1] : '--');
  const [day, setDay] = React.useState(prev ? prev[2] : '');
  const [year, setYear] = React.useState(prev ? prev[3] : '');
  const [pending, setPending] = React.useState(!prev);
  const [error, setError] = React.useState('');
  const [ageLabel, setAgeLabel] = React.useState<string>("");

  const enteredDateMs = new Date(`${month}/${day}/${year}`).getTime();

  const {
    showAge,
    minAllowedTimeInPast,
    maxAllowedTimeInPast,
    minAllowedTimeInFuture,
    maxAllowedTimeInFuture,
  } = props.question;

  const getTimeMs = (time: { amount: number, unit: 'days' | 'weeks' | 'months' | 'years' }) => {
    const { amount, unit } = time;
  
    switch (unit) {
      case 'days': return amount * msPerDay;
      case 'weeks': return amount * 7 * msPerDay;
      case 'months': return amount * (365.25/12) * msPerDay;
      case 'years': return amount * 365.25 * msPerDay;
      default: return 0;
    }
  }

  const dateDiffMs = props.referenceDateMs - enteredDateMs; // negative if entered date is in the future

  const getValidStatus = () => {
    // User has not yet typed input in every field
    if (pending) return 'incomplete';
    // User has entered a date that doesn't exist
    if (DateTime.fromISO(`${year}-${month}-${day}`).invalidReason) return 'invalid';

    if (maxAllowedTimeInPast && dateDiffMs > getTimeMs(maxAllowedTimeInPast)) return 'tooFarInPast';
    if (minAllowedTimeInPast && dateDiffMs < getTimeMs(minAllowedTimeInPast)) return 'tooRecentInPast';
    if (maxAllowedTimeInFuture && -dateDiffMs > getTimeMs(maxAllowedTimeInFuture)) return 'tooFarInFuture';
    if (minAllowedTimeInFuture && -dateDiffMs < getTimeMs(minAllowedTimeInFuture)) return 'tooSoonInFuture';

    return 'valid';
  }

  const errorMessageMap = {
    invalid: L.questions.household.invalid_date,
    tooFarInPast: maxAllowedTimeInPast?.errorMessage?.[context.lang],
    tooFarInFuture: maxAllowedTimeInFuture?.errorMessage?.[context.lang],
    tooRecentInPast: minAllowedTimeInPast?.errorMessage?.[context.lang],
    tooSoonInFuture: minAllowedTimeInFuture?.errorMessage?.[context.lang]
  };

  const validStatus = getValidStatus();

  useEffect(() => {
    if (validStatus === 'incomplete') return;

    const ageNumber = Math.floor(dateDiffMs / (365.25 * msPerDay)); // considering leap years

    const handleValidDate = () => {
      setError('');
      props.updateMember(props.question.key, `${month}/${day}/${year}`);
      if (showAge) {
        props.updateMember('age', String(ageNumber));
        setAgeLabel(getAgeLabel(ageNumber));
      }
    };

    const handleInvalidDate = () => {
      setError(errorMessageMap[validStatus as keyof typeof errorMessageMap] || L.questions.household.invalid_date);
      props.updateMember(props.question.key, '');
      if (showAge) {
        props.updateMember('age', '');
        setAgeLabel('');
      }
    }

    if (validStatus === 'valid') {
      handleValidDate();
    } else {
      handleInvalidDate();
    }
  }, [day, month, year, validStatus]);

  const getAgeLabel = (ageNumber: number) => {
    const ageDescription = ageNumber <= 1
      ? L.questions.household.year_old
      : L.questions.household.years_old;
  
    return `${ageNumber < 1 ? "< 1" : ageNumber} ${ageDescription}`;
  };

  const monthOptions = useMemo(() => [
    { label: L.months.jan, value: "01" },
    { label: L.months.feb, value: "02" },
    { label: L.months.mar, value: "03" },
    { label: L.months.apr, value: "04" },
    { label: L.months.may, value: "05" },
    { label: L.months.jun, value: "06" },
    { label: L.months.jul, value: "07" },
    { label: L.months.aug, value: "08" },
    { label: L.months.sep, value: "09" },
    { label: L.months.oct, value: "10" },
    { label: L.months.nov, value: "11" },
    { label: L.months.dec, value: "12" }
  ], [L.months]);

  const selectedMonth = useMemo(() => 
    monthOptions.find((monthOption) => monthOption.value === month), 
  [monthOptions, month]
  );
  
  return (
    <fieldset>
      <label className="text-sm font-medium">
        {props.label}
        {!props.question.optional && (
          <>
            <span className="text-red-600" aria-hidden="true"> *</span>
            <span className="sr-only"> required</span>
          </>
        )}
      </label>
      <div className="relative flex justify-between gap-2 sm:gap-3 w-full sm:visible sm:flex-wrap">
        <div className="flex-grow basis-32">
          <Select
            description=""
            label={L.questions.household.month}
            labelLocation="above"
            value={selectedMonth}
            onChange={(option) => setMonth(option.value)}
            options={monthOptions}
            disabled={props.blockQuestionEdits}
          />
        </div>
        <div>
          <Input
            label={L.questions.household.day}
            type="number"
            value={day}
            onChange={(e) => setDay(e.target.value.slice(0, 2))}
            onBlur={(e) => {
              if (e.target.value.length === 1) {
                setDay("0" + e.target.value);
              }
            }}
            min={1} 
            max={31}
            placeholder={L.date.dd}
          />
        </div>
        <div className="flex-grow basis-8">
          <Input
            pattern="[1-2][0-9]{3}"
            label={L.questions.household.year}
            type="number"
            placeholder={L.date.yyyy}
            value={year}
            onChange={(e) => {
              setYear(e.target.value.slice(0, 4));
              if (e.target.value.length === 4) setPending(false);
            }}
            onBlur={(e) => {
              if (e.target.value.length > 0) setPending(false);
            }}
          />
        </div>
      </div>
      {showAge && ageLabel && (
        <div className="text-sm mt-2 font-medium text-gray-500">
          {ageLabel}
        </div>
      )}
      {error && !pending && (
        <div className="text-sm">
          <ValidatorMessage message={error} />
        </div>
      )}
    </fieldset>
  );
}

export const HouseholdContext = React.createContext<{ parentProps: QuestionProps, householdDispatch: React.Dispatch<HouseholdAction> } | undefined>(undefined);

export default function HouseholdCalculation(props: QuestionProps) {
  const context = useContext(InterfaceContext);
  const L = useLocalizedStrings();
  const { toast } = useToast();
  const { roles, title, showYou, memberCountLabel, referenceDate } = useMemo<{
    roles: Role[];
    title: Text;
    info: InfoDict;
    showYou?: {
      nameField: string;
      birthdateField?: string | undefined;
    };
    memberCountLabel?:{
      singular: Text;
      plural: Text;
    };
    referenceDate?: Date;
  }>(() => safeParse(props.Metadata || "{}"), [props.Metadata]);
  const householdInfo = useMemo<Record<string, HouseholdMember[]>>(() => {
    const info = props.info[props["Target Field"] || ""];
    return safeParse(info || "{}");
  }, [props.info]);

  const reducer = (
    state: Record<string, HouseholdMember[]>,
    action: HouseholdAction
  ) => {
    switch (action.type) {
      case "add":
        return {
          ...state,
          [action.payload.roleType]: [
            ...(state[action.payload.roleType] || []),
            action.payload,
          ],
        };
      case "edit":
        const stateAfterEditing =
          state[action.payload.roleType]?.map((member: HouseholdMember) => {
            return member.id === action.payload.id ? action.payload : member;
          }) || [];
        return {
          ...state,
          [action.payload.roleType]: stateAfterEditing,
        };
      case "delete":
        const stateAfterDeleting = (state[action.payload.roleType] || []).filter(
          (member: HouseholdMember) => member.id !== action.payload.id
        );
        if (stateAfterDeleting.length === 0) {
          const {
            [action.payload.roleType]: value,
            ...stateWithDeletedKeyRemoved
          } = state;
          return stateWithDeletedKeyRemoved;
        } else {
          return {
            ...state,
            [action.payload.roleType]:
              state[action.payload.roleType].filter(
                (member: HouseholdMember) => member.id !== action.payload.id
              ) || [],
          };
        }
      default:
        return state;
    }
  };

  // householdState represents the current form state for all household members
  // It contains all required keys for each member with all valid values (some values may be empty strings)
  const [householdFormState, householdDispatch] = useReducer(
    reducer,
    householdInfo || {}
  );

  // This is used to trigger the component to rerender (which saves info and allows proceeding with the survey)
  // if the applicant is prepopulated via showYou, and there is no one else they need to add to their household
  const [noOthers, setNoOthers] = useState(false);

  /**
   * This returns whether the two states have the same number of household members for each role in the first state passed in
   */
  const hhStatesHaveSameNumberOfMembers = useCallback((baseState: Record<string, HouseholdMember[]>, compareState: Record<string, HouseholdMember[]>) => {
    return Object.entries(baseState).every(([roleType, members]) => compareState[roleType]?.length === members.length);
  }, []);

  // householdValidState represents members with complete information (no empty values)
  const householdValidState = useMemo(() => {
    const hhState = Object.keys(householdFormState).reduce((newState: Record<string, HouseholdMember[]>, roleType: string) => {
      const dataCollectOptions = roles.find((r: Role) => r.type === roleType)?.dataCollectOptions;
      const validMembers = householdFormState[roleType].filter(
        (member: HouseholdMember) => {
          return memberIsValid(member, props.info, dataCollectOptions)
        }
      );
      // exclude empty arrays from the final state
      if (validMembers.length > 0) {
        newState[roleType] = validMembers;
      }
      return newState;
    }, {});

    if (showYou?.nameField) {
      hhState['system_you'] = [{
        id: 'you',
        roleType: 'system_you',
        name: props.info[showYou.nameField],
        birth_date: showYou?.birthdateField ? props.info[showYou.birthdateField] : undefined
      }];

      // This is used to make sure we change the value in the Household Calculator's target field if the name or birthdate that it is
      // pulling in changes
      const infoChanged =
        !!householdInfo['system_you'] && (householdInfo['system_you'][0].name !== props.info[showYou.nameField]
          || householdInfo['system_you'][0].birth_date !== (showYou.birthdateField && props.info[showYou.birthdateField]));

      const memberCount = Object.values(householdInfo).reduce((total: number, role: HouseholdMember[]) => total + role.length, 0);
      const hhStateIsValid = hhStatesHaveSameNumberOfMembers(householdFormState, hhState);

      // If we have never set the target field or something has changed, set it now.
      // Also wait to save until the user either hits "No Others" or adds someone besides themself.
      // We also don't want to save if the current state is invalid - that is, if some household member was removed
      if (props['Target Field']
          && hhStateIsValid
          && (!props.info[props['Target Field']] || infoChanged)
          && (noOthers === true || (memberCount > (showYou ? 1 : 0)))) {
        props.setInfoKey(
          props['Target Field'],
          JSON.stringify(hhState),
          true,
          false,
        );
      }
    }
    return hhState;
  }, [householdFormState, noOthers, showYou?.nameField && props.info[showYou.nameField], showYou?.birthdateField && props.info[showYou.birthdateField], props.info, hhStatesHaveSameNumberOfMembers]);

  const currentStateIsFullyValid = useMemo(
    () => hhStatesHaveSameNumberOfMembers(householdFormState, householdValidState), 
    [householdFormState, householdValidState, hhStatesHaveSameNumberOfMembers],
  );

  useEffect(() => {
    // Generate forms for members with startOpen option that don't have any info added yet
    const rolesWithStartOpen = roles.filter((role: Role) => {
      return role.displayOptions.startOpen && !householdInfo[role.type];
    });
    for (const roleInfo of rolesWithStartOpen){
      householdDispatch({
        type: "add",
        payload: initializeMemberInfo(roleInfo) as HouseholdMember,
      });
    }
  }, []);

  const householdCount = Object.values(householdValidState).reduce((total: number, role: HouseholdMember[]) => {
    return total + role.length;
  }, 0);

  const previousValidState = useRef(JSON.stringify(householdValidState));

  useEffect(() => {
    if (props.blockQuestionEdits) return;

    const newValidState = JSON.stringify(householdValidState);
    // We only want to set the info key if the current state is fully valid
    if (previousValidState.current !== newValidState && currentStateIsFullyValid) {
      previousValidState.current = newValidState;
      props.setInfoKey(
        props["Target Field"]!,
        newValidState !== "{}" ? newValidState : "",
        true,
        false,
      );
      props.setInfoKey(
        props["Target Field"] + "_count",
        householdCount === 0 ? "" : JSON.stringify(householdCount),
        true,
        false
      );
    }
  }, [householdValidState, currentStateIsFullyValid, props.blockQuestionEdits]);

  const label =
    householdCount === 1
      ? memberCountLabel?.singular[context.lang] || L.questions.household.household_member
      : memberCountLabel?.plural[context.lang] || L.questions.household.household_members;
  const householdCountText = `${householdCount} ${label}`;

  const initializeMemberInfo = useCallback((roleInfo: Role): HouseholdMember => {
    // Initialize memberInfo object with all required keys for this applicant
    // Empty string values represent info to be collected
    const memberInfo: HouseholdMember = {
      id: v4(),
      roleType: roleInfo.type,
    };

    const {
      collectBirthdate,
      dependence,
      customQuestions,
      hideName,
      requireFullName
    } = roleInfo.dataCollectOptions || {};

    if (!hideName) {
      if (requireFullName === 'Split first and last') {
        memberInfo.first_name = "";
        memberInfo.last_name = "";
      } else {
        memberInfo.name = "";
      }
      
    }
    if (collectBirthdate === "full") {
      memberInfo.birth_date = "";
    }
    if (collectBirthdate === "year_only") {
      memberInfo.birth_year = "";
    }
    if (dependence) {
      if (dependence === "collect") {
        memberInfo.dependence = "";
      } else {
        memberInfo.dependence = dependence.preset;
      }
    }
    if (customQuestions) {
      for (const customQuestion of customQuestions) {
        memberInfo[getKey(customQuestion)] = "";
      }
    }
    return memberInfo;
  }, []);

  const referenceDateMs = getReferenceDateMs(referenceDate, props.info);

  useEffect(() => {
    if (referenceDateMs === 0) {
      toast({
        description: `Household calcuator reference date is invalid: ${referenceDate}`,
        variant: 'error'
      });
    }
  }, [referenceDateMs]);

  return (
    <HouseholdContext.Provider value={{ parentProps: props, householdDispatch }}>
      <div className="flex flex-col gap-3">
        <div className="w-full border bg-white shadow rounded-lg px-2.5 text-lg ">
          <h4 className="sm:mx-3 mt-4 mb-4 pb-2 border-b border-gray-300">{title?.[context.lang] || L.questions.household.your_household}</h4>
          {showYou && (
            <MemberDisplayGroup
              label={L.questions.income.person.you}
              members={[
                {
                  id: "you",
                  roleType: "system_you",
                  name: props.info[showYou?.nameField],
                  birth_date: showYou?.birthdateField ? props.info[showYou.birthdateField] : undefined,
                },
              ]}
            />
          )}

          {Object.keys(householdFormState).filter((roleType: string) => roleType !== 'system_you').map((roleType: string) => (
            <MemberFormGroup
              info={props.info}
              key={roleType}
              referenceDateMs={referenceDateMs}
              roleInfo={roles.find((role: Role) => role.type === roleType) || { type: '', label: { singular: {} }, dataCollectOptions: {}, displayOptions: {} } as Role}
              membersWithForm={householdFormState[roleType] || []}
              membersSaved={householdInfo[roleType] || []}
              householdDispatch={householdDispatch}
              initializeMemberInfo={initializeMemberInfo}
              blockQuestionEdits={props.blockQuestionEdits}
            />
          ))}

          {/* Buttons for member types with no forms */}
          {!props.blockQuestionEdits && <div className="flex flex-wrap gap-2 my-2 sm:mx-3 ">
            {roles
              .filter((roleInfo: Role) => !householdFormState[roleInfo.type]?.length)
              .map((roleInfo: Role, index: number) => (
                <Button
                  key={roleInfo.type + "_" + index}
                  onClick={() => householdDispatch({
                    type: "add",
                    payload: initializeMemberInfo(roleInfo) as HouseholdMember,
                  })}
                >
                  {L.questions.household.add} {roleInfo.label.singular[context.lang]}
                </Button>
              ))
            }
          </div>}
          {(showYou && householdCount === 1 && !noOthers) && <div className="flex flex-wrap gap-2 my-2 sm:mx-3 ">
            <Button
              onClick={() => setNoOthers(true)}
            >
              {L.questions.household.no_others}
            </Button>
          </div>
          }

          <div className="mt-3 sm:mx-3 font-bold text-lg text-gray-700 mb-4 py-2 border-t border-gray-300">
            {householdCountText}
          </div>
        </div>
      </div>
    </HouseholdContext.Provider>
  );
}

type HouseholdQuestionProps<Q extends HouseholdCalculatorQuestion> = {
  key: string;
  question: Q & CommonProperties;
  index: number;
  memberValidState: HouseholdMember;
  memberDispatch: React.Dispatch<MemberAction>;
  blockQuestionEdits?: boolean;
}

const CustomSelectQuestion = ({ question, index, memberValidState, memberDispatch, blockQuestionEdits } : HouseholdQuestionProps<CustomSelect>) => {

  const context = useContext(InterfaceContext);
  const L = useLocalizedStrings();

  const options: SelectOptionType[] = question.choices.map((choice) => ({ value: choice.value, label: (choice.label)[context.lang as keyof RichText] } as SelectOptionType));
  return (
    <div key={question.key + "_" + index} className="w-full flex flex-col sm:flex-row sm:items-center">
      <Select 
        disabled={blockQuestionEdits}
        label={
          <>
            {(question.content)[context.lang as keyof RichText]}
            {!question.optional && (
              <>
                <span className="text-red-600 ml-1" aria-hidden="true">*</span>
                <span className="sr-only ml-1">{L.component_library.input.required}</span>
              </>
            )}
          </>
        }
        labelLocation="above"
        description=""
        options={options}
        value={options.find((option) => option.value === memberValidState[question.key]) ?? { label: "---", value: ""} as SelectOptionType}
        onChange={(option) => {
          if (!option) return;
          memberDispatch({
            type: "set",
            payload: { key: question.key, value: option.value },
          });
        }}
      />
    </div>
  );
}

export const SelectFromDataQuestion = ({ question, index, memberValidState, memberDispatch, blockQuestionEdits }: HouseholdQuestionProps<SelectFromData>) => {
  const householdContext = useContext(HouseholdContext);
  const L = useLocalizedStrings();

  const Question = memberValidState.id + '_' + question.targetField + '_' + index;

  const setInfoKey = useCallback((key: string, value: string, valid: boolean, disqualifies: boolean) => {
    memberDispatch({
      type: 'set',
      payload: { key, value }
    })
  }, [index, Question, memberValidState, memberDispatch]);

  const info = useMemo(() => {
    return { [question.sourceField]: householdContext?.parentProps.info[question.sourceField] || '', 
      ...memberValidState };
  }, [memberValidState, question.sourceField, householdContext?.parentProps.info[question.sourceField]]);

  const adjustedContent = useMemo(() => {
    return Object.keys(question.content).reduce((acc, key) => {
      let text = question.content[key as keyof RichText] || '';
      // Add the "Required" slug if the question is not optional
      if (!question.optional) {
        text += ' <span class="text-red-600">*</span><span class="sr-only"> required</span>';
      }
      if (key == 'en') {
        acc['English Content'] = text;
      }
      if (key == 'es') {
        acc['Spanish Content'] = text;
      }
      acc[key] = text;
      return acc;
    }, {} as { 'English Content': string, 'Spanish Content': string, [key: string]: string });
  }, [question.content, question.optional]);

  const qType = question.multiple ? 'Multiple Select' : 'Single Select';
  const qProps: QuestionProps = {
    key: Question,
    uid: householdContext?.parentProps.uid || '',
    // MultipleSelect needs to know only the info that is needed by the source field
    info,
    setInfoKey,
    Viewer: householdContext?.parentProps.Viewer || 'applicant',
    Question,
    'Field Type': qType,
    'Target Field': question.targetField,
    'Additional Options': ['Formatted'],
    Metadata: JSON.stringify({
      max_allowed_selections: question.maxAllowedSelections,
      source_field: question.sourceField,
    }),
    ...adjustedContent,
    blockQuestionEdits: blockQuestionEdits
  }

  const block = question.multiple 
    ? <MultipleSelectQuestion {...qProps} /> 
    : <SingleSelectQuestion {...qProps} />;

  return <div className={classNames(`w-full flex flex-col sm:flex-row text-sm sm:items-center py-2 text-gray-700`,
    index === 0 ? 'border-b-2' : 'border-t-2 border-b-2'
  )}>
    {block}
  </div>
  
}

const HouseholdQuestionMap = {
  'Select': CustomSelectQuestion,
  'SelectFromData': SelectFromDataQuestion,
} as const;

